import type { ListBuilderConfiguration } from '../../types';
import {
	createDatesColumn,
	createOrderErpNumbersColumn,
	createOrderLinesColumn,
	createOrderTotalsColumn,
	createStatusOriginColumn,
} from '../order-client-embedded/columns';
import { createClientInfoColumn } from '../order/columns';

/**
 * PD-15423 — Deliveries Received (cross-account delivery).
 *
 * Embedded on the client detail page's Orders tab: orders placed by OTHER
 * clients whose delivery destination is THIS client. The host component
 * scopes the query with the `fk_client__id_delivery` context param, so the
 * columns lead with the ORDERING client (who placed and pays for the order)
 * — the delivery destination is the page's own client.
 */
export const ORDER_DELIVERIES_RECEIVED_CONFIG: ListBuilderConfiguration = {
	objectType: 'order',
	lists: [
		{
			id: 'deliveries-received',
			cacheTTL: 600000,
			name: 'Deliveries Received',
			type: 'default',
			active: true,
			sortOrder: 1,
			columns: [
				createClientInfoColumn(),
				createStatusOriginColumn(),
				createOrderErpNumbersColumn(),
				createDatesColumn(),
				createOrderLinesColumn(),
				createOrderTotalsColumn(),
			],
			description:
				"Orders placed by other clients for delivery to this client's site",
			defaultSortId: 'date-desc',
			requiredFields: [
				'_id',
				'externalId',
				'externalId2',
				'date',
				'_modified_date',
				'status',
				'origin',
				'numberOfLines',
				'tax',
				'freight',
				'discount',
				'discountType',
				'subtotal',
				'total',
			],
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [20, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'orderKeywords',
			'orderStatus',
			'orderDateRange',
			'orderModifiedDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/orders/:id',
			objectLabel: 'Order',
			objectTable: 'order',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'externalId2', fieldLabel: 'External ID 2' },
				{ fieldName: 'date', fieldLabel: 'Date' },
				{ fieldName: 'clientName', fieldLabel: 'Ordering Client' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'numberOfLines', fieldLabel: 'Number of Lines' },
				{ fieldName: 'total', fieldLabel: 'Total' },
				{ fieldName: 'customerPO', fieldLabel: 'Customer PO' },
			],
		},
	],
	tableSorts: [
		{
			id: 'date-desc',
			name: 'Date (Newest First)',
			fields: [{ direction: 'DESC', fieldName: 'date', objectTable: 'order' }],
			description: 'Sort orders by date, newest first',
		},
		{
			id: 'date-asc',
			name: 'Date (Oldest First)',
			fields: [{ direction: 'ASC', fieldName: 'date', objectTable: 'order' }],
			description: 'Sort orders by date, oldest first',
		},
		{
			id: 'total-desc',
			name: 'Total (Highest First)',
			fields: [{ direction: 'DESC', fieldName: 'total', objectTable: 'order' }],
			description: 'Sort orders by total amount, highest first',
		},
	],
};
